import { Key, ReactNode } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { Button, Grid, P } from '@farewill/ui'
import { BORDER, COLOR, GTR } from '@farewill/ui/tokens'

import EstateCard from 'components/estate-card'
import { ConfirmModal } from 'components/confirm-modal'
import { showModal } from 'state/actions'

import SectionHeading, { SectionToggle } from './section-heading'

// height/flex-direction fill the row height (paired with FieldsGrid below)
// so the notes divider lands at the same bottom edge across a row of cards
const StyledEstateCard = styled(EstateCard)`
  margin: 0;
  border: 1px solid ${COLOR.GREY.LIGHT};
  border-radius: ${BORDER.RADIUS.M};
  height: 100%;
  display: flex;
  flex-direction: column;
`

const StyledButton = styled(Button.Secondary)`
  margin-top: ${GTR.M};
`

// fills the stretched card height above; align-content keeps the fields
// packed at the top instead of spreading out, and grid-auto-flow keeps
// them in order - the Grid's dense default pulls a later half-width field
// up beside an earlier one
export const FieldsGrid = styled(Grid)`
  flex: 1;
  align-content: start;
  grid-auto-flow: row;
`

export type CollectionRecord = {
  id: number
  attributes: Record<string, unknown>
}

type Props<Row extends CollectionRecord> = {
  heading?: string
  scrollId?: string
  type: string
  records: Row[]
  isAdding?: boolean
  onAdd: () => unknown
  addLabel: string
  onDelete: (id: number) => unknown
  renderRow: (record: Row, layout: { isFullWidth: boolean }) => ReactNode
  toggle?: SectionToggle
  summary?: ReactNode
  fullWidth?: boolean
  getCardHeading?: (record: Row, index: number) => string | undefined
  canDelete?: (record: Row) => boolean
  getRecordKey?: (record: Row) => Key
  getDeleteConfirmation?: (
    record: Row,
    cardName: string
  ) => DeleteConfirmation | null
}

export type DeleteConfirmation = {
  message: ReactNode
  confirmButtonText: string
  confirmButtonSubmittingText: string
}

const defaultDeleteConfirmation = (
  _record: CollectionRecord,
  cardName: string
): DeleteConfirmation => ({
  message: `${cardName} will be deleted. This can’t be undone.`,
  confirmButtonText: 'Yes, delete',
  confirmButtonSubmittingText: 'Deleting...',
})

const CollectionSection = <Row extends CollectionRecord>({
  heading,
  scrollId,
  type,
  records,
  isAdding,
  onAdd,
  addLabel,
  onDelete,
  renderRow,
  toggle,
  summary,
  fullWidth = false,
  getCardHeading,
  canDelete = () => true,
  getDeleteConfirmation = defaultDeleteConfirmation,
  getRecordKey = (record) => record.id,
}: Props<Row>) => {
  const dispatch = useDispatch()
  const isFullWidth = fullWidth || records.length === 1

  const getCardName = (record: Row, index: number) =>
    getCardHeading?.(record, index)?.replace(/ of \d+$/, '') ??
    `${type} ${index + 1}`

  const confirmDelete = (record: Row, index: number) => {
    const confirmation = getDeleteConfirmation(
      record,
      getCardName(record, index)
    )
    if (!confirmation) return onDelete(record.id)

    dispatch(
      showModal({
        headingText: 'Are you sure?',
        component: () => (
          <ConfirmModal
            onConfirm={async () => {
              await onDelete(record.id)
            }}
            confirmButtonText={confirmation.confirmButtonText}
            confirmButtonSubmittingText={
              confirmation.confirmButtonSubmittingText
            }
          >
            <P>{confirmation.message}</P>
          </ConfirmModal>
        ),
      })
    )
  }

  return (
    <div data-scroll-id={scrollId}>
      {heading && (
        <SectionHeading
          heading={heading}
          toggle={
            toggle && {
              ...toggle,
              disabled: toggle.disabled ?? records.length > 0,
            }
          }
        />
      )}
      {(!toggle || toggle.isOn) && (
        <>
          {summary}
          <Grid gap="M">
            {records.map((record, index) => (
              <Grid.Item span={isFullWidth ? 12 : 6} key={getRecordKey(record)}>
                <StyledEstateCard
                  type={type}
                  heading={getCardHeading?.(record, index)}
                  listPosition={index + 1}
                  listLength={records.length}
                  deleteLabel={`Delete ${getCardName(record, index)}`}
                  onTrashClick={
                    canDelete(record)
                      ? () => confirmDelete(record, index)
                      : undefined
                  }
                >
                  {renderRow(record, { isFullWidth })}
                </StyledEstateCard>
              </Grid.Item>
            ))}
          </Grid>
          <StyledButton
            type="button"
            size="S"
            // the collection hook shows a failed add's error, so it's
            // left there rather than going unhandled
            onClick={() => Promise.resolve(onAdd()).catch(() => undefined)}
            loading={isAdding}
          >
            {addLabel}
          </StyledButton>
        </>
      )}
    </div>
  )
}

export default CollectionSection
